import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditApplicationTask, FullCreditApplication } from '../../types'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import { transformCreditTaskFromApi, transformCreditTaskToApi } from './credit-task-transform'
import { CreditTaskDto } from './CreditApplicationTaskDto'

const BASE_URL = '/Applications'

// Query Keys
export const creditTaskKeys = {
  all: ['creditTask'] as const,
  byId: (financingProgramId: string, creditApplicationId: string, id: string) =>
    [...creditTaskKeys.all, 'byId', financingProgramId, creditApplicationId, id] as const,
  list: (financingProgramId: string, creditApplicationId: string) =>
    [...creditTaskKeys.all, 'list', financingProgramId, creditApplicationId] as const,
}

// ============================================================================
// GET OPERATIONS
// ============================================================================

async function getTaskByCreditApplicationId(
  financingProgramId: string,
  creditApplicationId: string,
): Promise<CreditApplicationTask[]> {
  const apiClient = getApiClient()
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/tasks`
  const response = await apiClient.get<CreditTaskDto[]>(url)
  return response.data.map((task) => transformCreditTaskFromApi(task))
}

async function getTaskById(
  financingProgramId: string,
  creditApplicationId: string,
  taskId: string,
): Promise<CreditApplicationTask> {
  const apiClient = getApiClient()
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/tasks/${taskId}`
  const response = await apiClient.get<CreditTaskDto>(url)
  return transformCreditTaskFromApi(response.data)
}

// Query Hooks for GET operations
export function useGetTaskByCreditApplicationId(financingProgramId?: string, creditApplicationId?: string) {
  return useQuery({
    queryKey: creditTaskKeys.list(financingProgramId ?? '', creditApplicationId ?? ''),
    queryFn: () => getTaskByCreditApplicationId(financingProgramId!, creditApplicationId!),
    enabled: !!financingProgramId && !!creditApplicationId,
  })
}

export function useGetTaskById(financingProgramId?: string, creditApplicationId?: string, taskId?: string) {
  return useQuery({
    queryKey: creditTaskKeys.byId(financingProgramId ?? '', creditApplicationId ?? '', taskId ?? ''),
    queryFn: () => getTaskById(financingProgramId!, creditApplicationId!, taskId!),
    enabled: !!financingProgramId && !!creditApplicationId && !!taskId,
  })
}

// ============================================================================
// CREATE OPERATION
// ============================================================================

async function createTask(data: CreditApplicationTask): Promise<CreditApplicationTask> {
  const apiClient = getApiClient()
  const { financingProgramId, creditApplicationId } = data
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/tasks`
  const response = await apiClient.post<CreditTaskDto>(url, transformCreditTaskToApi(data))
  return transformCreditTaskFromApi(response.data)
}

export function useCreateTask(): [MutationFunction<CreditApplicationTask, CreditApplicationTask>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createTask,
    onSuccess: (createdTask, params) => {
      const financingProgramId = params.financingProgramId!
      const creditApplicationId = createdTask.creditApplicationId!
      const taskWithFinancingProgram = { ...createdTask, financingProgramId }

      const queryKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          tasks: [...currentData.tasks, taskWithFinancingProgram],
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export async function createTaskAsync(data: CreditApplicationTask): Promise<CreditApplicationTask> {
  return createTask(data)
}

// ============================================================================
// UPDATE OPERATION
// ============================================================================

async function updateTask(data: CreditApplicationTask): Promise<CreditApplicationTask> {
  const apiClient = getApiClient()
  const { financingProgramId, creditApplicationId, id } = data
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/tasks/${id}`
  const response = await apiClient.put<CreditTaskDto>(url, transformCreditTaskToApi(data))
  return transformCreditTaskFromApi(response.data)
}

export function useUpdateTask(): [MutationFunction<CreditApplicationTask, CreditApplicationTask>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateTask,
    onSuccess: (updatedTask, params) => {
      const financingProgramId = params.financingProgramId!
      const creditApplicationId = updatedTask.creditApplicationId!

      const queryKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          tasks: currentData.tasks.map((t) =>
            t.id === updatedTask.id ? { ...updatedTask, financingProgramId, comments: t.comments } : t,
          ),
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export async function updateTaskAsync(data: CreditApplicationTask): Promise<CreditApplicationTask> {
  return updateTask(data)
}

// ============================================================================
// DELETE OPERATION
// ============================================================================

async function deleteTask(data: CreditApplicationTask): Promise<void> {
  const apiClient = getApiClient()
  const { financingProgramId, creditApplicationId, id } = data
  const url = `${BASE_URL}/${financingProgramId}/${creditApplicationId}/tasks/${id}`
  await apiClient.delete(url)
}

export function useDeleteTask(): [MutationFunction<void, CreditApplicationTask>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteTask,
    onSuccess: (_result, params) => {
      const financingProgramId = params.financingProgramId!
      const creditApplicationId = params.creditApplicationId!
      const taskId = params.id!

      const queryKey = fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId as EFinancingProgram)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          tasks: currentData.tasks.filter((t) => t.id !== taskId),
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export async function deleteTaskAsync(data: CreditApplicationTask): Promise<void> {
  return deleteTask(data)
}
